import styles from './MarkdownDocument.module.css'

type Block =
  | { type: 'h1' | 'h2' | 'h3' | 'p' | 'hr'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'table'; rows: string[][] }

function inlineHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
}

function parseMarkdown(markdown: string): Block[] {
  const blocks: Block[] = []
  const lines = markdown.replace(/\r\n/g, '\n').split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i] ?? ''
    const trimmed = line.trim()
    if (!trimmed) {
      i += 1
      continue
    }
    if (trimmed === '---') {
      blocks.push({ type: 'hr', text: '' })
      i += 1
      continue
    }
    if (trimmed.startsWith('# ')) {
      blocks.push({ type: 'h1', text: trimmed.slice(2) })
      i += 1
      continue
    }
    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: trimmed.slice(3) })
      i += 1
      continue
    }
    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: trimmed.slice(4) })
      i += 1
      continue
    }
    if (trimmed.startsWith('|')) {
      const rows: string[][] = []
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        const cells = (lines[i] ?? '')
          .trim()
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((cell) => cell.trim())
        if (!cells.every((cell) => /^:?-{3,}:?$/.test(cell))) {
          rows.push(cells)
        }
        i += 1
      }
      if (rows.length) blocks.push({ type: 'table', rows })
      continue
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const items: string[] = []
      while (i < lines.length && /^[-*] /.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().slice(2))
        i += 1
      }
      blocks.push({ type: 'ul', items })
      continue
    }

    const paragraph: string[] = [trimmed]
    i += 1
    while (i < lines.length) {
      const next = (lines[i] ?? '').trim()
      if (
        !next ||
        next === '---' ||
        next.startsWith('#') ||
        next.startsWith('|') ||
        next.startsWith('- ') ||
        next.startsWith('* ')
      ) {
        break
      }
      paragraph.push(next)
      i += 1
    }
    blocks.push({ type: 'p', text: paragraph.join(' ') })
  }
  return blocks
}

/** Lightweight Markdown renderer for the market agreement document. */
export function MarkdownDocument({ markdown }: { markdown: string }) {
  const blocks = parseMarkdown(markdown)
  return (
    <div className={styles.doc}>
      {blocks.map((block, index) => {
        if (block.type === 'hr') return <hr key={index} className={styles.hr} />
        if (block.type === 'h1') {
          return (
            <h2
              key={index}
              className={styles.h1}
              dangerouslySetInnerHTML={{ __html: inlineHtml(block.text) }}
            />
          )
        }
        if (block.type === 'h2') {
          return (
            <h3
              key={index}
              className={styles.h2}
              dangerouslySetInnerHTML={{ __html: inlineHtml(block.text) }}
            />
          )
        }
        if (block.type === 'h3') {
          return (
            <h4
              key={index}
              className={styles.h3}
              dangerouslySetInnerHTML={{ __html: inlineHtml(block.text) }}
            />
          )
        }
        if (block.type === 'ul') {
          return (
            <ul key={index} className={styles.ul}>
              {block.items.map((item) => (
                <li key={item} dangerouslySetInnerHTML={{ __html: inlineHtml(item) }} />
              ))}
            </ul>
          )
        }
        if (block.type === 'table') {
          return (
            <table key={index} className={styles.table}>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell) => (
                      <td key={cell} dangerouslySetInnerHTML={{ __html: inlineHtml(cell) }} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
        return (
          <p
            key={index}
            className={styles.p}
            dangerouslySetInnerHTML={{ __html: inlineHtml(block.text) }}
          />
        )
      })}
    </div>
  )
}
